import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Reveal } from '@/components/motion';
import { CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { public_api } from '@/lib/api';
import { SERVICES, CONTACT } from '@/data/site';

export default function Quote() {
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    service_type: params.get('service') || 'NDIS Transport',
    pickup_suburb: '', dropoff_suburb: '',
    estimated_distance_km: '', frequency: 'one-off',
    passengers: 1, wheelchair_accessible: false,
    funding_source: 'NDIS', additional_info: '',
    full_name: '', email: '', phone: '',
  });
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form,
        passengers: Number(form.passengers) || 1,
        estimated_distance_km: form.estimated_distance_km ? Number(form.estimated_distance_km) : undefined,
      };
      await public_api.quote(payload);
      setDone(true);
      toast.success("Quote request received — we'll email you shortly.");
    } catch { toast.error('Something went wrong.'); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <section className="pt-16 md:pt-24 pb-32" data-testid="quote-success">
        <div className="container-x max-w-2xl text-center">
          <div className="w-20 h-20 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-8">
            <CheckCircle size={40} weight="fill" className="text-brand" />
          </div>
          <h1 className="h-section text-balance">Quote request received.</h1>
          <p className="body-lg mt-6 text-pretty">
            Thanks {form.full_name}. Our team will prepare a tailored quote and email it to you within one business day.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/" className="btn-primary">Back to home</Link>
            <Link to="/book" className="btn-secondary">Book now</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-16 md:pt-24 pb-24" data-testid="quote-page">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="overline mb-6">Instant quote</div>
            <h1 className="h-display text-balance">Get a tailored quote — no obligation.</h1>
            <p className="body-md mt-6 text-pretty max-w-md">
              Tell us a few basics and we'll email you a fully tailored quote within one business day.
              For NDIS transport, we quote to the current NDIS Price Guide.
            </p>
            <div className="mt-8 p-6 rounded-2xl bg-neutral-50 border border-neutral-200 text-sm text-ink-soft">
              <div className="font-semibold text-ink mb-2">Prefer to talk?</div>
              Call us on <a href={`tel:${CONTACT.phone_dial}`} className="text-brand font-semibold">{CONTACT.phone_display}</a> — quotes over the phone in minutes.
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="p-8 md:p-10 rounded-3xl bg-neutral-50 border border-neutral-200 space-y-5" data-testid="quote-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="label-base">Service type</label>
                  <select value={form.service_type} onChange={set('service_type')} data-testid="quote-service" className="input-base">
                    {SERVICES.map(s => <option key={s.slug}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-base">Frequency</label>
                  <select value={form.frequency} onChange={set('frequency')} data-testid="quote-frequency" className="input-base">
                    <option value="one-off">One-off</option>
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="monthly">Monthly</option>
                    <option value="recurring-other">Recurring (other)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="label-base">Pickup suburb</label>
                  <input required value={form.pickup_suburb} onChange={set('pickup_suburb')} data-testid="quote-pickup" className="input-base" placeholder="e.g. Richmond VIC" />
                </div>
                <div>
                  <label className="label-base">Drop-off suburb</label>
                  <input required value={form.dropoff_suburb} onChange={set('dropoff_suburb')} data-testid="quote-dropoff" className="input-base" placeholder="e.g. Fitzroy VIC" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className="label-base">Passengers</label>
                  <input type="number" min="1" value={form.passengers} onChange={set('passengers')} data-testid="quote-passengers" className="input-base" />
                </div>
                <div>
                  <label className="label-base">Est. distance (km)</label>
                  <input value={form.estimated_distance_km} onChange={set('estimated_distance_km')} data-testid="quote-distance" className="input-base" placeholder="optional" />
                </div>
                <div>
                  <label className="label-base">Funding source</label>
                  <select value={form.funding_source} onChange={set('funding_source')} data-testid="quote-funding" className="input-base">
                    <option>NDIS</option><option>TAC</option><option>WorkSafe</option>
                    <option>Aged Care</option><option>Private</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-3 p-4 rounded-xl border border-neutral-200 bg-white hover:border-brand cursor-pointer">
                <input type="checkbox" checked={form.wheelchair_accessible} onChange={set('wheelchair_accessible')} data-testid="quote-wheelchair" className="w-4 h-4 accent-brand" />
                <span className="font-medium text-ink text-sm">Wheelchair accessible vehicle required</span>
              </label>
              <div>
                <label className="label-base">Anything else we should know?</label>
                <textarea rows={3} value={form.additional_info} onChange={set('additional_info')} data-testid="quote-info" className="input-base resize-none" placeholder="Anything specific about the trip or the passenger." />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-4 border-t border-neutral-200">
                <div>
                  <label className="label-base">Your name</label>
                  <input required value={form.full_name} onChange={set('full_name')} data-testid="quote-name" className="input-base" />
                </div>
                <div>
                  <label className="label-base">Email</label>
                  <input required type="email" value={form.email} onChange={set('email')} data-testid="quote-email" className="input-base" />
                </div>
                <div>
                  <label className="label-base">Phone</label>
                  <input required value={form.phone} onChange={set('phone')} data-testid="quote-phone" className="input-base" />
                </div>
              </div>
              <button type="submit" disabled={busy} data-testid="quote-submit" className="btn-primary w-full md:w-auto">
                {busy ? 'Sending…' : 'Request quote'} <ArrowRight size={16} weight="bold" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
